import { registryFor, ZEUS_UI_ID, clip, toKey } from './registry'

export const ELEMENT_LIMIT = 80
const LABEL_MAX = 60
const NOTE_MAX = 120

// jsdom 没有布局（offsetParent 恒为 null），按样式沿祖先判断可见性，覆盖 v-show 与 hidden。
export function isVisible(el, frameEl) {
  if (!el?.isConnected) return false
  if (frameEl && !isVisible(frameEl)) return false
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    if (node.hidden) return false
    const style = node.ownerDocument.defaultView?.getComputedStyle(node)
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return false
  }
  return true
}

function scopeOf(entry, root) {
  if (!entry.frame) return { doc: root, frameEl: null }
  const frameEl = root.querySelector(entry.frame)
  try {
    const doc = frameEl?.contentDocument
    return doc ? { doc, frameEl } : null
  } catch { return null } // 跨源 iframe 读不到，直接跳过
}

function expand(entry, doc) {
  if (!entry.each) return [[entry.id, doc.querySelector(entry.selector)]]
  const nodes = [...doc.querySelectorAll(entry.each)].filter(el => !entry.filter || entry.filter(el))
  return nodes.map((el, index) => {
    const key = toKey(entry.key ? entry.key(el, index) : index + 1)
    return [key ? entry.id.replace('*', key) : '', el]
  })
}

/** 注册表 × 实时 DOM → 可见元素（带 DOM 引用，宿主内部用）。 */
export function resolveEntries(pageKey, root = document) {
  const seen = new Set()
  const out = []
  for (const entry of registryFor(pageKey)) {
    const scope = scopeOf(entry, root)
    if (!scope) continue
    for (const [id, el] of expand(entry, scope.doc)) {
      if (!el || !ZEUS_UI_ID.test(id) || seen.has(id) || !isVisible(el, scope.frameEl)) continue
      seen.add(id)
      out.push({ id, el, entry, frameEl: scope.frameEl })
    }
  }
  return out
}

export const findElement = (pageKey, id, root = document) => resolveEntries(pageKey, root).find(item => item.id === id)

export function isDisabled({ el, entry }) {
  const control = entry.control?.(el) || el
  // :disabled 覆盖 fieldset[disabled] 里的控件；采集与执行共用这一个判断。
  return Boolean(control.disabled === true || control.matches?.(':disabled') || el.getAttribute('aria-disabled') === 'true' || el.closest('[inert]'))
}

/** 发给 iframe 的结构：只有界面文字，不含 DOM、单元格、日志。 */
export function describeElement(target) {
  const { id, el, entry } = target
  const state = { ...(entry.state?.(el) || {}) }
  if (isDisabled(target)) {
    state.disabled = true
    const reason = clip(entry.reason?.(el), NOTE_MAX)
    if (reason) state.reason = reason
  }
  for (const key of Object.keys(state)) if (state[key] === undefined || state[key] === false) delete state[key]
  const label = clip(typeof entry.label === 'function' ? entry.label(el) : entry.label, LABEL_MAX) || clip(id, LABEL_MAX)
  const describe = entry.describe ? clip(entry.describe(el), NOTE_MAX) : ''
  return {
    id, kind: entry.kind, label, ops: [...entry.ops],
    ...(Object.keys(state).length ? { state } : {}),
    ...(entry.section ? { section: entry.section } : {}),
    ...(describe ? { describe } : {}),
  }
}

export function collectElements(pageKey, root = document) {
  const all = resolveEntries(pageKey, root)
  return {
    pageKey,
    items: all.slice(0, ELEMENT_LIMIT).map(describeElement),
    ...(all.length > ELEMENT_LIMIT ? { truncated: true } : {}),
  }
}
